import { api } from '@/services';
import { hasResponseError } from '@/utils';
import { NURSE_STATUS } from '@/utils/config';
import { useAppMessageStore } from '@/stores/appMessage';
import { useAuthStore } from '@/stores/auth';

// Firebase imports
import { ref } from 'vue';
import { initializeApp } from "firebase/app";
import { firebaseConfig } from "@/firebaseConfig";
import {
  isSupported, getMessaging, onMessage,
  getToken, deleteToken
} from "firebase/messaging";

const NOTIF_ICON = 'https://helloyta.com/api/icons/notification_logo.png';

const showOsNotification = (title = 'Hello YTa', body = '') => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(title, { body, icon: NOTIF_ICON });
    });
  } else {
    new Notification(title, { body, icon: NOTIF_ICON });
  }
};

const createPusherChannel = (authStore, appMessageStore) => {
  if (!authStore || !appMessageStore) return {};

  const USER_STATUS = { ...NURSE_STATUS };

  const createMessageChannelByPusher = async () => {
    if (
      authStore.user?.status === USER_STATUS.authenticating ||
      authStore.user?.status === USER_STATUS.unauthenticated
    ) return false;

    const pusher = new Pusher(import.meta.env.VITE_AUTH_KEY_PUSHER, {
      cluster: 'ap1'
    });
  
    let channelToken = authStore.channelToken;
    if (!channelToken) {
      const userRole = localStorage.getItem('mode');
      if (userRole) {
        console.log('userRole', userRole);
        const res = await api.post(`${userRole}/notification-token/register`, {
          notifType: 'pusher_channel'
        });
        if (hasResponseError(res)) {
          appMessageStore.setErrorModal({
            modalMessage: res,
          });
  
          return false;
        }
  
        authStore.setChannelToken(res.data?.token);
        channelToken = authStore.channelToken || 'no-token-found';
      }
    }
    const channel = pusher.subscribe(channelToken);
  
    channel.bind('UPDATE_BOOKING_EVENT', function (data) {
      authStore.setNotifStatus(true);
      authStore.setSignalOfRequests(true);
      showOsNotification(data.title, data.body);
    });

    channel.bind('REMINDER_BOOKING_EVENT', function (data) {
      authStore.setNotifStatus(true);
      showOsNotification(data.title, data.body);
    });
  };

  return {
    createMessageChannelByPusher
  }
};

const createFirebaseChannel = (appMessageStore) => {
  if (!appMessageStore) return {};

  const firebaseApp = initializeApp(firebaseConfig);
  let messaging = null;

  const createMessageChannelByFirebase = async () => {
    if (typeof Notification === 'undefined' || !navigator.serviceWorker) return;

    const firebaseSupported = await isSupported();
    if (!firebaseSupported) return;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    // Register SW explicitly so we can pass it to getToken
    let swRegistration = null;
    try {
      swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
      await navigator.serviceWorker.ready;
    } catch (err) {
      console.error('SW registration failed:', err);
      return;
    }

    if (!messaging) {
      messaging = getMessaging(firebaseApp);
      onMessage(messaging, (payload) => {
        const { title, body } = payload.notification || {};
        showOsNotification(title, body);
      });
    }

    const rawVapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!rawVapidKey) {
      console.warn('VITE_FIREBASE_VAPID_KEY is not set — FCM push notifications disabled.');
      return;
    }
    const vapidKey = rawVapidKey.trim().replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    if (!/^[A-Za-z0-9\-_]{30,}$/.test(vapidKey)) {
      console.warn('VITE_FIREBASE_VAPID_KEY appears invalid — FCM push notifications disabled.');
      return;
    }

    const userRole = localStorage.getItem('mode');
    try {
      const currentToken = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration,
      });
      if (currentToken && userRole) {
        const res = await api.post(`${userRole}/fcm-token/register`, {
          token: currentToken,
          oldToken: null,
        });
        if (hasResponseError(res)) {
          appMessageStore.setErrorModal({ modalMessage: res });
          deleteToken(messaging);
        }
      }
    } catch (err) {
      console.error('FCM token error:', err);
    }
  };

  return { createMessageChannelByFirebase };
};

export function useRealtimeChannel () {
  const appMessageStore = useAppMessageStore();
  const authStore = useAuthStore();

  const isHtmlPromptShowed = ref(false);
  const pusherChannel = createPusherChannel(authStore, appMessageStore);
  const firebaseChannel = createFirebaseChannel(appMessageStore);
  /*-----Functions-----*/
  const setSignalOfNewMessages = async () => {
    authStore.setNotifStatus(authStore.user?.hasUnreadNotification);
  };

  const closeHtmlPrompt = () => {
    firebaseChannel.createMessageChannelByFirebase();
  };
  /*-------------------*/
  return {
    isHtmlPromptShowed,

    setSignalOfNewMessages,
    closeHtmlPrompt,
    ...pusherChannel,
    ...firebaseChannel,
  }
};
